"use client";
import { useState, useEffect } from "react";
import { Jumbotron } from "components";
import {
  Flex,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
} from "@chakra-ui/react";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import Link from "next/link";
import { usePaymentStatus } from "./CryptoPaymentsModal/queries";
import { useGetStripeSessionStatus } from "queries";

export const PaymentSuccess = ({
  payment_method = "paypal",
  payment_id = undefined,
}: {
  payment_method?: string;
  payment_id?: string;
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const { data: dataStatus } = usePaymentStatus(
    mounted ? payment_method : undefined
  );

  const { data: stripeSessionStatus } = useGetStripeSessionStatus(
    mounted ? payment_method : undefined,
    mounted ? payment_id : undefined
  );

  const balanceUpdateMessage =
    payment_method === "crypto" || payment_method === "stripe"
      ? "Your balance will be updated shortly"
      : "You should see your new balance in 30 seconds. Sometimes it can take up to 15 minutes due to payment provider delay. If you don't see your credits by then, please contact us.";

  useEffect(() => {
    if (!mounted) return;

    const trackPaymentSuccess = () => {
      if (payment_method === "crypto") {
        if (typeof window !== "undefined" && window.gtag) {
          window.gtag("event", "crypto_payment_success", {
            event_category: "Crypto Payments",
            event_label: "Success",
            value: 1,
          });
        }
      }
    };

    trackPaymentSuccess();
  }, [payment_method, dataStatus, mounted]);

  useEffect(() => {
    if (!mounted) return;

    if (
      payment_method === "stripe" &&
      stripeSessionStatus !== undefined &&
      typeof window !== "undefined" &&
      window.gtag
    ) {
      if (
        stripeSessionStatus.status === "complete" &&
        stripeSessionStatus.payment_intent_id !== null
      ) {
        const lastGTagTrackedPaymentIntentID = localStorage.getItem(
          "lastGTagTrackedPaymentIntentID"
        );

        if (
          lastGTagTrackedPaymentIntentID ===
          stripeSessionStatus.payment_intent_id
        ) {
          console.log("This payment is already tracked by GTag");
        } else {
          console.log("Tracking payment with GTag");

          window.gtag("event", "purchase", {
            transaction_id: stripeSessionStatus.payment_intent_id,
            value: stripeSessionStatus.price,
            currency: "USD",
            payment_method: "Stripe",
            items: [
              {
                id: stripeSessionStatus.plan_name,
                name: stripeSessionStatus.plan_name,
                quantity: 1,
                price: stripeSessionStatus.price,
              },
            ],
          });

          localStorage.setItem(
            "lastGTagTrackedPaymentIntentID",
            stripeSessionStatus.payment_intent_id
          );

          console.log("Successfully tracked payment with GTag");
        }
      }
    }
  }, [payment_method, stripeSessionStatus, mounted]);

  // Show nothing during initial mount
  if (!mounted) {
    return null;
  }

  return (
    <Flex direction="column">
      <Jumbotron height="100%">
        <Flex
          h="250px"
          direction="column"
          align="center"
          textAlign="center"
          justify="center"
        >
          <Text
            fontSize={{ base: "38px", lg: "58px" }}
            fontWeight="semibold"
            style={{
              background:
                "linear-gradient(to right,  #FFE1FF 40%, #A4DBEE 60%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              MozBackgroundClip: "text",
            }}
            w={{ base: "310px", lg: "700px" }}
          >
            Payment Success
          </Text>
          {payment_method === "crypto" && dataStatus?.payment_id && (
            <Text>Your order number is {dataStatus.payment_id}</Text>
          )}
          {payment_method === "stripe" && stripeSessionStatus !== undefined && (
            <Text>
              A confirmation email has been sent to {stripeSessionStatus.email}
            </Text>
          )}
          <Text mt="20px">{balanceUpdateMessage}</Text>
        </Flex>
      </Jumbotron>
      <Flex w="100%" justify="center">
        <CustomAccordion />
      </Flex>
      <Box h="150px" />
    </Flex>
  );
};

const CustomAccordion = () => {
  const faqData = [
    {
      question: "What is DeepMode and how does it work?",
      answer:
        "DeepMode is a Generative AI platform that helps creators make their AI clones. Creators can generate beautiful AI photos, whether realistic or non-realistic, with unlimited freedom. With DeepMode, creators can unleash their imagination and create unique photos with unlimited freedom.",
    },
    {
      question: "How much does DeepMode cost?",
      answer: (
        <Text>
          DeepMode offers various top-up plans: Check{" "}
          <Link style={{ textDecoration: "underline" }} href="/payments">
            this page
          </Link>{" "}
          for more details.
        </Text>
      ),
    },
    {
      question: "Can I try DeepMode for free?",
      answer:
        "Yes, every new user automatically gets 20 credits. However, to unlock training an AI model you will need to top-up your balance.",
    },
    {
      question: "Are my DeepMode creations private?",
      answer:
        "Yes, your creations on DeepMode are kept private and only you can view them. You also have the option to delete specific models or images you don't like.",
    },
    {
      question: "Does DeepMode store my uploaded images?",
      answer:
        "No, your uploaded photos are deleted immediately from our servers after training. In addition, the Model itself is being deleted from our servers after 30 days. If you wish to extend the model storage, you can do so.",
    },
    {
      question: "Can I use my creations anywhere?",
      answer:
        "Yes. Your generated images are yours to use on any other platform.",
    },
    {
      question:
        "How is DeepMode different from Midjourney, Stable Diffusion, or Dall-E?",
      answer:
        "Unlike other AI platforms such as Midjourney, Stable Diffusion, or DALL-E, DeepMode stands out for its exclusive focus on the generation of AI clones. DeepMode is committed to providing a fully uncensored experience, allowing users unlimited creative freedom without any content restrictions.",
    },
    {
      question: "Does DeepMode run on the Cloud?",
      answer:
        "Yes, DeepMode runs in the cloud. This means that you can use the platform and view your generations across various devices.",
    },
    {
      question: "Does DeepMode offer an Affiliate program?",
      answer:
        "Absolutely! Become a partner and earn cash rewards every month. Find out more about our Affiliate Program.",
    },
    {
      question: "Can I create videos with DeepMode?",
      answer:
        "Currently, video generation is not supported. However, we're working on this feature and plan to introduce it soon.",
    },
    {
      question: "Can I get a refund?",
      answer:
        "We offer refunds for purchases made within the last 7 days, provided no credits have been used. If you believe you're eligible for a refund, please contact us here.",
    },
    {
      question: "How do I get in touch with the DeepMode team?",
      answer: (
        <Text>
          We usually hangout on{" "}
          <a
            style={{ textDecoration: "underline" }}
            href="https://discord.gg/CMwtccKthy"
          >
            Discord
          </a>
          . You can also reach out to us via{" "}
          <a
            style={{ textDecoration: "underline" }}
            href="mailto:info@deepmode.com"
          >
            e-mail
          </a>
          .
        </Text>
      ),
    },
  ];

  return (
    <Flex
      mt="250px"
      mb="150px"
      px={["35px", "35px", "40px", "60px", "60px", "15%"]}
      direction="column"
    >
      <Text fontSize="40px" fontWeight="semibold">
        FAQ
      </Text>
      <Accordion mt="10px" allowToggle>
        {faqData.map((item, index) => (
          <AccordionItem key={index}>
            {({ isExpanded }) => (
              <>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    {item.question}
                  </Box>
                  {isExpanded ? (
                    <MinusIcon fontSize="12px" />
                  ) : (
                    <AddIcon fontSize="12px" />
                  )}
                </AccordionButton>
                <AccordionPanel pb={4}>{item.answer}</AccordionPanel>
              </>
            )}
          </AccordionItem>
        ))}
      </Accordion>
    </Flex>
  );
};

export default PaymentSuccess;